import { ComponentProps, useRef } from "react";

import { Ds, VisuallyHidden } from "@accurx/design";
import { WithTooltip } from "domains/patient/components/Tooltip/WithTooltip";
import { useIsEllipsisActive } from "domains/patient/hooks/useIsEllipsisActive";
import { formatAdditionalPatientDemographics } from "domains/patient/utils/format/patientDisplayInfo";

import {
    StyledContainer,
    StyledFlex,
    StyledNameText,
} from "./PatientName.styles";

type TextProps = Omit<ComponentProps<typeof Ds.Text>, "children">;

export type PatientNameProps = {
    name: string;
    ageYear?: number;
    gender: string;
    textProps?: TextProps;
};

export const PatientName = ({
    name,
    ageYear,
    gender,
    textProps,
}: PatientNameProps) => {
    const nameRef = useRef<HTMLElement>(null);
    const isEllipsisActive = useIsEllipsisActive(nameRef.current);

    const demographicDetails = formatAdditionalPatientDemographics({
        gender,
        ageYear,
    });

    return (
        <StyledContainer>
            <StyledFlex gap="0.5">
                <VisuallyHidden as="span">Patient: {name}, </VisuallyHidden>
                <WithTooltip
                    isEnabled={isEllipsisActive}
                    content={
                        <Ds.Text size="small" weight="bold" color="white">
                            {name}
                        </Ds.Text>
                    }
                    props={{
                        content: {
                            align: "start",
                            side: "bottom",
                        },
                    }}
                >
                    <StyledNameText
                        size="small"
                        weight="bold"
                        aria-hidden="true"
                        ref={nameRef}
                        mb="0px"
                        {...textProps}
                    >
                        {name}
                    </StyledNameText>
                </WithTooltip>
                <VisuallyHidden as="span">
                    {`${ageYear ?? ""}${
                        gender !== "NotKnown" && gender !== "Unknown"
                            ? `,  ` + gender.toLowerCase()
                            : ", "
                    },`}
                </VisuallyHidden>
                <Ds.Text
                    size="small"
                    weight="bold"
                    mb="0px"
                    aria-hidden="true"
                    {...textProps}
                >
                    {demographicDetails}
                </Ds.Text>
            </StyledFlex>
        </StyledContainer>
    );
};
